import {
  Card, Collapse, Row, Form, Col, Input, Select, Cascader, InputNumber, Button, Table, message, Popconfirm,
} from 'antd';
import { any } from 'prop-types';
import { useState, useRef } from 'react';
import { isEmpty, isNaN } from 'lodash';
import { map } from 'lodash/collection';
import FarmingForm from './FarmingForm';
import { addDecimalNum, moneyDelete } from '@/utils/utils';
import { LGG_MANAGER_TYPE, LGG_ACCOUNT_TYPE } from '@/utils/mappingDict';

const rowFormItemLayout = {
  labelCol: {
    sm: { span: 4 },
  },
  wrapperCol: {
    sm: { span: 24 },
  },
};

const getAgeFromIdCard = (idCard) => {
  if (!/^\d{17}[\dXx]$/.test(idCard)) return null;
  const birthStr = idCard.slice(6, 14);
  const birthYear = Number(birthStr.slice(0, 4));
  const birthMonth = Number(birthStr.slice(4, 6)) - 1;
  const birthDay = Number(birthStr.slice(6, 8));
  const birthDate = new Date(birthYear, birthMonth, birthDay);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthYear;
  if (
    today.getMonth() < birthMonth
    || (today.getMonth() === birthMonth && today.getDate() < birthDay)
  ) {
    age--;
  }

  return age;
};

const { Panel } = Collapse;
function SubjectBasicInfo({
  formRef, dict,
}) {
  const farmingFormRef = useRef(null);
  const [dataSource, setDataSource] = useState([]);
  // 根据区县计算补贴面积
  const getArea = (newData) => {
    const grouped = {};

    newData?.forEach((item) => {
      const id = item.areaId;
      if (!grouped[id]) {
        grouped[id] = {
          wheatArea: 0, othersArea: 0, subsidyArea: 0, farmArea: 0,
        };
      }
      grouped[id].wheatArea = addDecimalNum([
        grouped[id].wheatArea,
        item.wheatArea,
      ]);

      grouped[id].othersArea = addDecimalNum([
        grouped[id].othersArea,
        item.riceArea,
        item.cornArea,
        item.potatoArea,
        item.buckwheatArea,
        item.highlandBarleyArea,
      ]);

      grouped[id].subsidyArea = Math.max(
        grouped[id].wheatArea,
        grouped[id].othersArea,
      );

      grouped[id].farmArea = addDecimalNum([
        grouped[id].farmArea,
        item.contractedArea,
        item.leasedArea,
      ]);

      grouped[id].areaCode = item.addr;
    });

    const total = addDecimalNum(Object.values(grouped).map((area) => area.subsidyArea)); // 总补贴面积

    // 先重置areaDetailVos字段，再设置新值
    formRef.setFieldValue('areaDetailVos', undefined);
    formRef.setFieldsValue({
      totalContractedArea: addDecimalNum(map(newData, (item) => item.contractedArea)), // 总承包耕地面积
      totalLeasedArea: addDecimalNum(map(newData, (item) => item.leasedArea)), // 总租种耕地面积
      totalPlantingArea: addDecimalNum(map(newData, (item) => item.zzmj1)), // 总种植面积
      totalSubsidyArea: total, // 总补贴面积
      detailVos: newData,
      areaDetailVos: grouped,
    });
    setDataSource(newData);
  };

  const onSubmit = (e) => {
    // 小麦-面积
    const wheatArea = moneyDelete(e.wheatArea);
    // 其他农作物-面积
    const ohterArea = addDecimalNum([e.riceArea, e.cornArea, e.potatoArea, e.highlandBarleyArea, e.buckwheatArea]);
    // 种植面积（所有农作物面积之和）
    const zzmj = addDecimalNum([wheatArea, ohterArea]);
    if (!zzmj) {
      message.error('种植品种面积不可全部为空或0');
      return;
    }

    const obj = {
      key: e.key ?? dataSource.length + 1, // 若有 key 使用原 key，否则新增 key
      zzmj1: zzmj,
      subsidyArea: Math.max(wheatArea, ohterArea),
    };
    let newData = [];

    if (e.key !== undefined && e.key !== null) {
      // 编辑模式
      const originalRecord = dataSource.find((item) => item.key === e.key);
      const isProvinceIdChanged = originalRecord
        && (originalRecord.provinceId !== e.provinceId
         || originalRecord.cityId !== e.cityId
         || originalRecord.areaId !== e.areaId);

      if (isProvinceIdChanged) {
        // 如果provinceId发生变化，同步更新所有数据的provinceId
        newData = dataSource.map((item) => {
          let updatedItem = {
            ...item,
            provinceId: e.provinceId,
            cityId: e.cityId,
            areaId: e.areaId,
            addr: e.addr,
          };

          // 如果需要清除其他数据，并且当前项不是正在编辑的项，则清除特定字段
          if (e.shouldClearOtherData && item.key !== e.key) {
            updatedItem = {
              ...updatedItem,
              potatoArea: undefined,
              buckwheatArea: undefined,
              highlandBarleyArea: undefined,
            };

            // 重新计算该条记录的种植面积和补贴面积
            const itemWheatArea = moneyDelete(updatedItem.wheatArea);
            const itemOtherArea = addDecimalNum(
              [updatedItem.riceArea,
                updatedItem.cornArea,
                0, // potatoArea 被清除
                0, // buckwheatArea 被清除
                0], // highlandBarleyArea 被清除
            );
            updatedItem.zzmj1 = addDecimalNum([itemWheatArea, itemOtherArea]);
            updatedItem.subsidyArea = Math.max(itemWheatArea, itemOtherArea);
          }

          // 如果是正在编辑的项，使用新的数据
          if (item.key === e.key) {
            updatedItem = { ...updatedItem, ...e, ...obj };
          }

          return updatedItem;
        });
      } else {
        // provinceId未变化，只更新当前记录
        newData = dataSource.map((item) => (item.key === e.key ? { ...item, ...e, ...obj } : item));
      }
    } else {
      // 新增
      newData = [...dataSource, { ...e, ...obj }];
    }
    getArea(newData);
    farmingFormRef.current?.close();
  };

  const del = (id) => {
    const filteredList = dataSource.filter((item) => item.key !== id);
    const newList = filteredList.map((item, index) => ({
      ...item,
      key: index + 1,
    }));
    getArea(newList);
    message.success('删除成功');
  };

  const columns = [
    {
      title: '序号',
      dataIndex: 'key',
      width: 60,
    },
    {
      title: '区域',
      dataIndex: 'addr',
      width: 180,
    },
    {
      title: '种粮地点',
      dataIndex: 'addressCountryside',
      render: (_, record) => (
        <span>
          {record.addressCountryside}
          {record.addressVillage}
        </span>
      ),
    },
    {
      title: '承包耕地面积(亩)',
      dataIndex: 'contractedArea',
    },
    {
      title: '租种耕地面积(亩)',
      dataIndex: 'leasedArea',
    },
    {
      title: '小麦(亩)',
      dataIndex: 'wheatArea',
    },
    {
      title: '水稻(亩)',
      dataIndex: 'riceArea',
    },
    {
      title: '玉米(亩)',
      dataIndex: 'cornArea',
    },
    {
      title: '马铃薯(亩)',
      dataIndex: 'potatoArea',
    },
    {
      title: '荞麦(亩)',
      dataIndex: 'buckwheatArea',
    },
    {
      title: '青稞(亩)',
      dataIndex: 'highlandBarleyArea',
    },
    {
      title: '操作',
      width: 100,
      render: (_, record) => (
        <>
          <a
            onClick={() => {
              farmingFormRef.current?.open({ ...record, isEditMode: true }); // 标识为编辑模式
            }}
            style={{ marginRight: 10 }}
          >
            编辑
          </a>
          <Popconfirm
            title="确定删除当前选项吗?"
            onConfirm={() => del(record.key)}
            okText="确定"
            cancelText="删除"
          >
            <a href="#">删除</a>
          </Popconfirm>
        </>
      ),
    },
  ];

  const isPersonal = [
    {
      pattern: /^\d{17}[\dXx]$/,
      message: '请输入18位有效身份证号',
    },
    {
      validator: (_, value) => {
        if (!value) return Promise.resolve();
        const age = getAgeFromIdCard(value);
        if (age < 15 || age > 80) {
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject('可申报范围（15～80岁）');
        }
        return Promise.resolve();
      },
    },
  ];
  return (
    <>
      <Card title="申报主体信息" className="form-info__box">
        <Collapse defaultActiveKey={['1-1']}>
          <Panel key="1-1" header="申报主体基本信息">
            <Row>
              <Col span={12}>
                <Form.Item
                  name="applicantName"
                  label="姓名/单位"
                  rules={[
                    {
                      required: true,
                      message: '姓名/单位',
                    },
                    { pattern: /^\s*\S.*$/, message: '姓名/单位' },
                  ]}
                >
                  <Input placeholder="单位名称/姓名" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="applicantNo"
                  label="身份证号/机构代码"
                  rules={[
                    {
                      required: true,
                      message: '身份证号/机构代码',
                    },
                    ...(dict.userType === 2 ? isPersonal : []),
                  ]}
                >
                  <Input placeholder="身份证号/机构代码" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="registrationLocation"
                  label="注册地点/户籍所在地"
                  rules={[
                    {
                      required: true,
                      message: '请选择项注册地点/户籍所在地',
                    },
                  ]}
                >
                  <Cascader
                    options={dict?.area}
                    placeholder="请选择项注册地点/户籍所在地"
                    fieldNames={{ label: 'name', value: 'id', children: 'children' }}
                    onChange={
                      (value) => {
                        // 赋值给后端字段
                        formRef.setFieldsValue({
                          subjectBasicInfo: {
                            registrationLocation: value.join(','),
                            cityOrState: +value[1],
                          },
                        });
                      }
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="detailAddress"
                  label="联系地址"
                  rules={[
                    {
                      required: true,
                      message: '请输入联系地址',
                    },
                  ]}
                >
                  <Input placeholder="请输入联系地址" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="managementEntityType"
                  label="经营主体类型"
                  rules={[
                    {
                      required: true,
                      message: '请选择经营主体类型',
                    },
                  ]}
                >
                  <Select
                    placeholder="经营主体类型"
                    options={dict[LGG_MANAGER_TYPE]}
                    fieldNames={{ label: 'name', value: 'val' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="applicationYear"
                  label="申报年度"
                  rules={[
                    {
                      required: true,
                      message: '请选择申报年度',
                    },
                  ]}
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="contactor"
                  label="联系人"
                  rules={[
                    {
                      required: true,
                      message: '请输入联系人',
                    },
                  ]}
                >
                  <Input placeholder="请输入联系人" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="telephone"
                  label="联系电话"
                  rules={[
                    {
                      required: true,
                      message: '请输入联系电话',
                    },
                    { pattern: /(^1[3-9]\d{9}$)|(^(\d{3,4}-?|\d{3,4}\))?\d{7,8}$)/, message: '电话号码格式错误' },
                  ]}
                >
                  <Input placeholder="请输入联系电话" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="depositBank"
                  label="开户银行"
                  rules={[
                    {
                      required: true,
                      message: '请输入开户银行',
                    },
                  ]}
                >
                  <Input placeholder="请输入补贴资金发放的开户银行" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="bankCardAccount"
                  label="银行卡账号"
                  rules={[
                    {
                      required: true,
                      message: '请输入银行卡账号',
                    },
                    { pattern: /^\s*\S.*$/, message: '银行卡账号' },
                  ]}
                >
                  <Input placeholder="请输入补贴资金发放的银行卡账号" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="accountType"
                  label="账户类型"
                  rules={[
                    {
                      required: true,
                      message: '请选择账户类型',
                    },
                  ]}
                >
                  <Select
                    placeholder="请选择账户类型"
                    options={dict[LGG_ACCOUNT_TYPE]}
                    fieldNames={{ label: 'name', value: 'val' }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="remark"
                  label="备注"
                  {...rowFormItemLayout}
                >
                  <Input.TextArea maxLength={500} showCount placeholder="请输入备注" />
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </Card>
      <Card title="耕种信息" className="form-info__box">
        <Collapse defaultActiveKey={['1-2']}>
          <Panel key="1-2" header="耕种面积">
            <Row>
              <Col span={12}>
                <Form.Item
                  name="totalContractedArea"
                  label="总承包耕地面积"
                  rules={[
                    {
                      required: true,
                      message: '请输入总承包耕地面积',
                    },
                  ]}
                >
                  <InputNumber placeholder="总承包耕地面积" addonAfter="亩" disabled style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="totalLeasedArea"
                  label="总租种耕地面积"
                  rules={[
                    {
                      required: true,
                      message: '请输入总租种耕地面积',
                    },
                  ]}
                >
                  <InputNumber placeholder="总租种耕地面积" addonAfter="亩" disabled style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="totalPlantingArea"
                  label="总种植面积"
                  rules={[
                    {
                      required: true,
                      message: '请输入总种植面积',
                    },
                  ]}
                >
                  <InputNumber placeholder="总种植面积" addonAfter="亩" disabled style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="totalSubsidyArea"
                  label="总补贴面积"
                  rules={[
                    {
                      required: true,
                      message: '请输入总补贴面积',
                    },
                  ]}
                >
                  <InputNumber placeholder="总补贴面积" addonAfter="亩" disabled style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="detailVos"
                  label="耕地面积详细情况（请以种植区域为单位提交申报）"
                  rules={[
                    {
                      required: true,
                      validator: () => {
                        if (isEmpty(dataSource)) {
                          return Promise.reject(new Error('请追加耕地面积详细情况'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Button
                    type="primary"
                    onClick={() => {
                      if (dataSource.length === 0) {
                        // 表格为空，可以任意选择provinceId
                        farmingFormRef.current?.open();
                      } else {
                        // 表格有数据，使用已有数据的provinceId
                        const firstRecord = dataSource[0];
                        const defaultData = {
                          provinceId: firstRecord.provinceId,
                          cityId: firstRecord.cityId,
                          areaId: firstRecord.areaId,
                          addr: firstRecord.addr,
                          isAddMode: true, // 标识为追加模式
                        };
                        farmingFormRef.current?.open(defaultData);
                      }
                    }}
                  >
                    追加
                  </Button>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Table size="small" columns={columns} dataSource={dataSource} pagination={false} scroll={{ y: 500 }} />
              </Col>
              <Col>
                <Form.Item
                  hidden
                  name="areaDetailVos"
                >
                  <InputNumber />
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </Card>
      <FarmingForm ref={farmingFormRef} onSubmit={onSubmit} dict={dict} />
    </>
  );
}

SubjectBasicInfo.propTypes = {
  formRef: any,
  area: any,
  dict: any,
};

export default SubjectBasicInfo;
